import { motion } from "framer-motion";
import { Target, Palette, Instagram, Camera, LayoutTemplate, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    id: "strategic-marketing",
    n: "01",
    title: "Strategic Marketing",
    icon: Target,
    desc: "Customized strategies that drive real results and grow your brand — built around your goals, not a template.",
    tags: ["Strategy", "Positioning", "Growth"],
    span: "md:col-span-7",
    tone: "light",
  },
  {
    id: "branding-logo",
    n: "02",
    title: "Branding & Logo Design",
    icon: Palette,
    desc: "Memorable brands that make a lasting impression — logo, colours, typography and the full identity system.",
    tags: ["Logo", "Identity", "Brand Board"],
    span: "md:col-span-5",
    tone: "dark",
  },
  {
    id: "social-media",
    n: "03",
    title: "Social Media Management",
    icon: Instagram,
    desc: "Consistent, engaging content that builds your presence and connects with your audience — we become your social partner.",
    tags: ["Instagram", "TikTok", "Community"],
    span: "md:col-span-5",
    tone: "beige",
  },
  {
    id: "content-creation",
    n: "04",
    title: "Content Creation",
    icon: Camera,
    desc: "Captions, copywriting, content planning and branded visuals that tell your story with intention.",
    tags: ["Copy", "Visuals", "Planning"],
    span: "md:col-span-7",
    tone: "light",
  },
  {
    id: "graphic-design",
    n: "05",
    title: "Graphic Design",
    icon: LayoutTemplate,
    desc: "Menus, flyers, posters, business cards, presentations and more — designed to perfection for print and digital.",
    tags: ["Print", "Digital", "Collateral"],
    span: "md:col-span-12",
    tone: "light",
  },
];

function toneClasses(tone) {
  switch (tone) {
    case "dark":
      return "bg-[#1C1B1A] text-[#FBF9F6] border-[#1C1B1A]";
    case "beige":
      return "bg-[#F2EEE9] text-[#1C1B1A] border-[#E5E0D8]";
    default:
      return "bg-[#FBF9F6] text-[#1C1B1A] border-[#E5E0D8]";
  }
}

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-40">
      <div className="max-w-7xl mx-auto w-full px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-6">
            <div className="flex items-center gap-3">
              <span className="w-8 h-px bg-[#1C1B1A]"></span>
              <span className="font-mono-label text-[#635F59]">Services</span>
            </div>
            <h2 className="mt-6 font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Full-service marketing,
              <br />
              <span className="italic text-[#B4A087]">designed to grow your business.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8">
            <p className="text-base md:text-lg text-[#635F59] leading-relaxed">
              We partner with local businesses to create marketing that is intentional,
              professional, and designed to help you grow. Every project is customized to
              reflect your brand and connect with your audience.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: idx * 0.06 }}
                className={`col-span-12 ${s.span} group relative border p-8 md:p-10 min-h-[300px] flex flex-col justify-between transition-all duration-500 hover:-translate-y-1 ${toneClasses(s.tone)}`}
              >
                <div className="flex items-start justify-end">
                  <Icon className={`${s.tone === "dark" ? "text-[#FBF9F6]" : "text-[#1C1B1A]"} opacity-80`} strokeWidth={1.25} size={28} />
                </div>
                <div>
                  <h3 className="font-serif-display text-2xl md:text-3xl leading-tight">{s.title}</h3>
                  <p className={`mt-4 text-sm md:text-base leading-relaxed max-w-md ${s.tone === "dark" ? "text-[#FBF9F6]/75" : "text-[#635F59]"}`}>{s.desc}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {s.tags.map((t) => (
                      <span key={t} className={`font-mono-label px-3 py-1 border ${s.tone === "dark" ? "border-[#FBF9F6]/25 text-[#FBF9F6]/80" : "border-[#1C1B1A]/20 text-[#635F59]"}`}>{t}</span>
                    ))}
                  </div>
                </div>
                <ArrowUpRight className={`absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${s.tone === "dark" ? "text-[#B4A087]" : "text-[#1C1B1A]"}`} size={22} strokeWidth={1.5} />
              </motion.article>
            );
          })}
        </div>

        <div className="mt-14 flex flex-col sm:flex-row gap-4">
          <a
            href="#pricing"
            data-testid="services-see-pricing-cta"
            className="inline-flex items-center justify-center gap-2 bg-[#1C1B1A] text-[#FBF9F6] px-7 py-4 text-sm hover:bg-[#33312E] transition-colors"
          >
            See services & pricing <span aria-hidden>→</span>
          </a>
          <a
            href="#contact"
            data-testid="services-consult-cta"
            className="inline-flex items-center justify-center gap-2 border border-[#1C1B1A] px-7 py-4 text-sm hover:bg-[#1C1B1A] hover:text-[#FBF9F6] transition-colors"
          >
            Book a free consultation
          </a>
        </div>
      </div>
    </section>
  );
}
